import { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import type { Content } from '@plone/types';
import type { BlockWrapperDataProps } from '../BlockWrapper/BlockWrapper';
import { BlockWrapper } from '../BlockWrapper/BlockWrapper';
import { messages } from './messages';
import { CardProps } from '@designsystem/stories/03-molecules/Card';
import { Listing } from '@designsystem/stories/04-organisms/Listing';
import {
  Sidepanel,
  SidepanelTabProps,
} from '@designsystem/stories/04-organisms/Sidepanel';
import { SidepanelContext } from '@designsystem/stories/04-organisms/SidepanelContext';
import {
  NormalizableContent,
  transformToCard,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import { isPlainClick } from '@fhnw/helpers/Url/isPlainClick';
import { SidepanelContent } from '../../SidepanelContent';

export interface BlockDataProps extends BlockWrapperDataProps {
  research_focus?: Content[];
}

interface ViewProps {
  block: string;
  data: BlockDataProps;
  content: Content;
  isEdit?: boolean;
  onChangeBlock: (block: string, newData: any) => void;
}

const Factory = (props: PropsWithChildren) => <>{props.children}</>;

const View = (props: ViewProps) => {
  const intl = useIntl();
  const _content = useSelector(
    (state: { content: { data: Content } }) => state.content.data,
  );
  const content =
    props?.content && (props.content?.['@id'] || props.content?.parent)
      ? props.content
      : _content;
  const data = { ...props?.data };
  const [open, doOpen] = useContext<any>(SidepanelContext);
  const [tab, setTab] = useState(0);
  const id = 'sidepanel-' + props.block;

  let updated = false;
  if (!data?.block_title) {
    data.block_title = intl.formatMessage(messages.Title);
    updated = true;
  }

  useEffect(() => {
    if (updated && props?.isEdit && props.onChangeBlock) {
      props.onChangeBlock(props.block, data);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  let research_focus = data?.research_focus;
  if (!research_focus || !research_focus.length) {
    research_focus = content?.research_focus;
  }

  const items: CardProps[] = [];
  const panels: SidepanelTabProps[] = [];

  (research_focus || []).forEach((focus) => {
    if (!focus) {
      return;
    }

    const i = items.length;
    const panelId = `${id}-tab-${i}`;
    const item = transformToCard(focus as NormalizableContent, intl, 'panel');
    const active = open === id && tab === i;

    item.onClick = (e: MouseEvent) => {
      if (!isPlainClick(e)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      doOpen(id);
      setTab(i);
    };
    item.linkClassName = 'ga-panel-forschungsschwerpunkt';
    item['aria-haspopup'] = 'true';
    item['aria-controls'] = panelId;
    item['aria-expanded'] = active ? 'true' : 'false';

    items.push(item);
    panels.push({
      label: focus?.short_title || (item.title as string),
      children: <SidepanelContent active={active} content={focus} />,
    });
  });

  if (!props?.isEdit && !items.length) {
    return null;
  }

  return (
    <BlockWrapper
      isEdit={props?.isEdit}
      block={props?.block}
      onChangeBlock={props?.onChangeBlock}
      data={data}
      factory={Factory}
    >
      <Sidepanel
        id={id}
        currentTab={tab}
        onTabChange={setTab}
        tabs={panels}
        close={intl.formatMessage(messages.Close)}
      />
      <Listing type="panel" items={items} />
    </BlockWrapper>
  );
};

export default View;
